import React, { createContext, useContext, useRef, useCallback } from 'react';
import { useAuth } from '../hooks';
import {
  useWebSocket,
  type ExchangeEvent,
  type ConnectionState,
} from '../hooks/useWebSocket';
import { env } from '../utils/env';

type EventListener = (event: ExchangeEvent) => void;

interface WebSocketContextValue {
  connectionState: ConnectionState;
  isConnected: boolean;
  connectionError: string | null;
  subscribe: (sessionId: string, agentName?: string) => void;
  unsubscribe: () => void;
  addListener: (listener: EventListener) => void;
  removeListener: (listener: EventListener) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { accessToken, isAuthenticated } = useAuth();
  const listenersRef = useRef<Set<EventListener>>(new Set());

  const handleEvent = useCallback((event: ExchangeEvent) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(event);
      } catch {
        /* listener errors should not crash the provider */
      }
    });
  }, []);

  const { connectionState, isConnected, subscribe, unsubscribe, connectionError } =
    useWebSocket({
      url: env.WS_ENDPOINT,
      token: accessToken ?? null,
      onEvent: handleEvent,
      enabled: isAuthenticated,
    });

  const addListener = useCallback((listener: EventListener) => {
    listenersRef.current.add(listener);
  }, []);

  const removeListener = useCallback((listener: EventListener) => {
    listenersRef.current.delete(listener);
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        connectionState,
        isConnected,
        connectionError,
        subscribe,
        unsubscribe,
        addListener,
        removeListener,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export function useWebSocketContext(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return ctx;
}
